import { useEffect, useState } from "react";
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  TextField, Button, Grid, Dialog, DialogTitle, DialogContent,
  DialogActions, Alert, IconButton, List, ListItem, ListItemText
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

import { sellBikeService } from "../service/SellBikeService";

const SellBike = () => {

  const [data, setData] = useState([]);
  const [filtered, setFiltered] = useState([]);

  const [filters, setFilters] = useState({
    model: "",
    colour: "",
    location: "",
    search: "",
  });

  const [open, setOpen] = useState(false);
  const [selectedBike, setSelectedBike] = useState(null);

  const [form, setForm] = useState({
    customerName: "",
    mobile: "",
    address: "",
    salePrice: "",

    cashAmount: "",
    financeCompany: "",
    financeAmount: "",

    exchange: {
      vehicleModel: "",
      engineNumber: "",
      chasisNumber: "",
      exchangeAmount: ""
    },

    gifts: [],

    expenses: {
      otherExpenses: ""
    }
  });

  // 🎁 temp entry used to build one gift before adding it to the list
  const [giftEntry, setGiftEntry] = useState({ name: "", quantity: "" });

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // 🚀 Fetch bikes
  const fetchStock = async () => {
    const res = await sellBikeService.getAvailableStock({
      ...filters,
      status: "IN_STOCK"
    });

    let bikes = [];
    res.data?.forEach((loc) => bikes.push(...loc.bikes));

    setData(bikes);
    setFiltered(bikes);
  };

  useEffect(() => {
    fetchStock();
  }, []);

  // 🔍 search
  const handleSearch = (value) => {
    setFilters({ ...filters, search: value });

    const filteredData = data.filter(
      (b) =>
        b.engine.toLowerCase().includes(value.toLowerCase()) ||
        b.chasis.toLowerCase().includes(value.toLowerCase())
    );

    setFiltered(filteredData);
  };

  // 🔥 open popup
  const openModal = (bike) => {
    setSelectedBike(bike);
    setOpen(true);
  };

  // 🔄 handle change
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleExchangeChange = (e) => {
    setForm({
      ...form,
      exchange: {
        ...form.exchange,
        [e.target.name]: e.target.value
      }
    });
  };

  // 💼 handle otherExpenses / expenses change
  const handleExpenseChange = (e) => {
    setForm({
      ...form,
      expenses: {
        ...form.expenses,
        [e.target.name]: e.target.value
      }
    });
  };

  // 🎁 gift entry input change
  const handleGiftEntryChange = (e) => {
    setGiftEntry({ ...giftEntry, [e.target.name]: e.target.value });
  };

  // 🎁 add current gift entry to the gifts array
  const handleAddGift = () => {
    if (!giftEntry.name || !giftEntry.quantity) return;

    setForm({
      ...form,
      gifts: [
        ...form.gifts,
        { name: giftEntry.name, quantity: Number(giftEntry.quantity) }
      ]
    });

    setGiftEntry({ name: "", quantity: "" });
  };

  // 🎁 remove a gift by index
  const handleRemoveGift = (index) => {
    setForm({
      ...form,
      gifts: form.gifts.filter((_, i) => i !== index)
    });
  };

  // 💰 SELL
  const handleSell = async () => {
    try {
      setError("");

      await sellBikeService.sellBike({
        bikeId: selectedBike._id,
        saleBikeEngineNumber: selectedBike.engine,
        saleBikeChasisNumber: selectedBike.chasis,
        ...form
      });

      setSuccess("Bike sold successfully ✅");
      setOpen(false);
      fetchStock();

    } catch (err) {
      setError(err.response?.data?.message || "Sell failed");
    }
  };

  return (
    <Box>
      <Typography variant="h4">Sell Bike</Typography>

      {/* 🔍 FILTER */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              label="Search Engine/Chasis"
              fullWidth
              onChange={(e) => handleSearch(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Button variant="contained" fullWidth onClick={fetchStock}>
              Apply Filter
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* 📊 TABLE */}
      <Paper>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Model</TableCell>
              <TableCell>Engine</TableCell>
              <TableCell>Chasis</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filtered.map((bike) => (
              <TableRow key={bike._id}>
                <TableCell>{bike.model}</TableCell>
                <TableCell>{bike.engine}</TableCell>
                <TableCell>{bike.chasis}</TableCell>
                <TableCell>{bike.location}</TableCell>

                <TableCell>
                  <Button
                    variant="contained"
                    onClick={() => openModal(bike)}
                  >
                    Sell
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* 🔥 SELL MODAL */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth>
        <DialogTitle>Sell Bike</DialogTitle>

        <DialogContent>

          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}

          <TextField label="Customer Name" name="customerName" fullWidth onChange={handleChange} sx={{ mt: 2 }} />
          <TextField label="Mobile" name="mobile" fullWidth onChange={handleChange} sx={{ mt: 2 }} />
          <TextField label="Address" name="address" fullWidth onChange={handleChange} sx={{ mt: 2 }} />

          <TextField label="Sale Price" name="salePrice" fullWidth onChange={handleChange} sx={{ mt: 2 }} />

          <Typography mt={2}>Payment</Typography>
          <TextField label="Cash Amount" name="cashAmount" fullWidth onChange={handleChange} sx={{ mt: 1 }} />
          <TextField label="Finance Company" name="financeCompany" fullWidth onChange={handleChange} sx={{ mt: 1 }} />
          <TextField label="Finance Amount" name="financeAmount" fullWidth onChange={handleChange} sx={{ mt: 1 }} />

          <Typography mt={2}>Exchange</Typography>
          <TextField label="Vehicle Model" name="vehicleModel" fullWidth onChange={handleExchangeChange} sx={{ mt: 1 }} />
          <TextField label="Engine Number" name="engineNumber" fullWidth onChange={handleExchangeChange} sx={{ mt: 1 }} />
          <TextField label="Chasis Number" name="chasisNumber" fullWidth onChange={handleExchangeChange} sx={{ mt: 1 }} />
          <TextField label="Exchange Amount" name="exchangeAmount" fullWidth onChange={handleExchangeChange} sx={{ mt: 1 }} />

          {/* 🎁 GIFTS (array — add multiple) */}
          <Typography mt={2}>Gifts</Typography>
          <Grid container spacing={1} sx={{ mt: 0 }}>
            <Grid item xs={6}>
              <TextField
                label="Gift Name"
                name="name"
                fullWidth
                value={giftEntry.name}
                onChange={handleGiftEntryChange}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Quantity"
                name="quantity"
                type="number"
                fullWidth
                value={giftEntry.quantity}
                onChange={handleGiftEntryChange}
              />
            </Grid>
            <Grid item xs={2}>
              <Button variant="outlined" fullWidth sx={{ height: "100%" }} onClick={handleAddGift}>
                Add
              </Button>
            </Grid>
          </Grid>

          {form.gifts.length > 0 && (
            <List dense>
              {form.gifts.map((g, index) => (
                <ListItem
                  key={index}
                  secondaryAction={
                    <IconButton edge="end" onClick={() => handleRemoveGift(index)}>
                      <DeleteIcon />
                    </IconButton>
                  }
                >
                  <ListItemText primary={`${g.name} x ${g.quantity}`} />
                </ListItem>
              ))}
            </List>
          )}

          {/* 💼 OTHER EXPENSES */}
          <Typography mt={2}>Other Expenses</Typography>
          <TextField
            label="Other Expenses"
            name="otherExpenses"
            fullWidth
            value={form.expenses.otherExpenses}
            onChange={handleExpenseChange}
            sx={{ mt: 1 }}
          />

        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSell}>
            Confirm Sale
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default SellBike;